import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Box,
  Chip,
  Checkbox,
  Tooltip,
  Toolbar,
  TextField,
  InputAdornment,
  IconButton,
  Alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";

// Component xử lý cắt chữ và Tooltip
const TruncatedCell = ({ text, maxWidth = 200, stickyStyle = {} }) => (
  <TableCell sx={{ maxWidth, ...stickyStyle }}>
    <Tooltip title={text} arrow placement="top">
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  </TableCell>
);

const ProductTableWithToolbar = () => {
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Dữ liệu mẫu
  const rows = useMemo(
    () =>
      Array.from({ length: 15 }, (_, index) => ({
        id: index + 1,
        name: `Sản phẩm mẫu ${
          index + 1
        } - Nội dung mô tả rất dài để kiểm tra tính năng hiển thị của bảng`,
        category: index % 2 === 0 ? "Điện tử" : "Gia dụng",
        price: (index + 1) * 100000,
        supplier: "Nhà cung cấp ABC chi nhánh Miền Nam Việt Nam",
      })),
    []
  );

  // Logic lọc dữ liệu theo tìm kiếm
  const filteredRows = rows.filter(
    (row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectAll = (e) =>
    setSelected(e.target.checked ? filteredRows.map((n) => n.id) : []);

  const handleSelectRow = (id) => {
    const idx = selected.indexOf(id);
    setSelected(
      idx === -1 ? [...selected, id] : selected.filter((i) => i !== id)
    );
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Styles cho Sticky Columns
  const stickyLeft = {
    position: "sticky",
    left: 0,
    backgroundColor: "#fff",
    zIndex: 2,
    boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
    width: 60,
  };
  const stickyRight = {
    position: "sticky",
    right: 0,
    backgroundColor: "#fff",
    zIndex: 2,
    boxShadow: "-2px 0 5px rgba(0,0,0,0.05)",
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
      <Paper sx={{ width: "100%", mb: 2, borderRadius: 2, overflow: "hidden" }}>
        {/* --- TOOLBAR --- */}
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            display: "flex",
            justifyContent: "space-between",
            backgroundColor:
              selected.length > 0 ? "rgba(25, 118, 210, 0.08)" : "white",
          }}
        >
          {selected.length > 0 ? (
            <Typography
              sx={{ flex: "1 1 100%" }}
              color="inherit"
              variant="subtitle1"
            >
              Đã chọn {selected.length} dòng
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flex: "1 1 100%",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Sản phẩm
              </Typography>
              <TextField
                size="small"
                placeholder="Tìm kiếm..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>
          )}

          {selected.length > 0 ? (
            <Tooltip title="Xóa">
              <IconButton color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button startIcon={<AddIcon />} variant="contained" size="small">
                Thêm
              </Button>
              <Tooltip title="Lọc danh sách">
                <IconButton>
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>

        {/* --- TABLE --- */}
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ ...stickyLeft, zIndex: 1100 }}
                >
                  <Checkbox
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < filteredRows.length
                    }
                    checked={
                      filteredRows.length > 0 &&
                      selected.length === filteredRows.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Tên sản phẩm</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Danh mục</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Giá bán</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Nhà cung cấp</TableCell>
                <TableCell
                  align="center"
                  sx={{ ...stickyRight, zIndex: 1100, fontWeight: "bold" }}
                >
                  Thao tác
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={isSelected(row.id)}
                  onClick={() => handleSelectRow(row.id)}
                  sx={{
                    cursor: "pointer",
                    "&.Mui-selected, &.Mui-selected:hover": {
                      "& .MuiTableCell-root": {
                        backgroundColor: "#f0f7ff !important",
                      },
                    },
                  }}
                >
                  <TableCell padding="checkbox" sx={stickyLeft}>
                    <Checkbox checked={isSelected(row.id)} />
                  </TableCell>
                  <TruncatedCell text={row.name} maxWidth={300} />
                  <TableCell>{row.category}</TableCell>
                  <TableCell>{row.price.toLocaleString()} đ</TableCell>
                  <TruncatedCell text={row.supplier} maxWidth={250} />
                  <TableCell
                    align="center"
                    sx={stickyRight}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button size="small">Sửa</Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    Không tìm thấy dữ liệu
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ProductTableWithToolbar;
